// Generated by Claude Code

/**
 * Module-scope capture of the PWA `beforeinstallprompt` event.
 *
 * The browser fires `beforeinstallprompt` **once, early** — usually before the
 * authenticated app shell (and its `InstallPrompt` component) has mounted. A
 * listener installed inside a late-mounting component's `useEffect` therefore
 * misses the event entirely. Registering at module scope (imported for its side
 * effect from `main.tsx` before React renders) captures the event the moment it
 * fires and stashes it so any later subscriber can still read it.
 */

/** The non-standard `beforeinstallprompt` event (not in the DOM lib types). */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  prompt: () => Promise<void>
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

/** Outcome of {@link promptInstall}. */
export type InstallPromptOutcome = 'accepted' | 'dismissed' | 'unavailable'

let deferredPrompt: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chromium's mini-infobar so we can drive our own affordance, and
    // stash the (single-use) event for whichever component subscribes later.
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    installed = false
    emit()
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    installed = true
    emit()
  })
}

/** Subscribe to capture-state changes. Returns an unsubscribe fn (for `useSyncExternalStore`). */
export function subscribeInstallPrompt(callback: () => void): () => void {
  listeners.add(callback)
  return () => { listeners.delete(callback) }
}

/** The captured, not-yet-used install prompt, or `null` when none is pending. */
export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt
}

/** True once the app has been installed during this session (`appinstalled` fired). */
export function isAppInstalled(): boolean {
  return installed
}

/**
 * Show the browser's native install dialog using the captured prompt.
 *
 * A `beforeinstallprompt` event can only be used once, so it is consumed
 * (cleared, subscribers notified) up front — the affordance hides immediately and
 * the event is never reused. Resolves to the user's choice, or `'unavailable'`
 * when no prompt was captured.
 */
export async function promptInstall(): Promise<InstallPromptOutcome> {
  const event = deferredPrompt
  if (!event) return 'unavailable'
  deferredPrompt = null
  emit()
  await event.prompt()
  try {
    const { outcome } = await event.userChoice
    return outcome
  } catch {
    // The user closed the native dialog without choosing.
    return 'dismissed'
  }
}
